import {
  Avatar,
  Box,
  IconButton,
  Stack,
  Text,
  styled,
} from '@ltvco/refresh-lib/theme';
import {
  Add as AddIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Person as PersonIcon,
} from '@mui/icons-material';

import { ChatMenuStack } from 'components/DeepSearch/components/components';
import {
  hideScrollbar,
  thinScrollbar,
} from 'components/DeepSearch/styleMixins';

export const MobileNavItemButton = styled('button')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '16px',
  cursor: 'pointer',
  border: 0,
  background: 'transparent',
  padding: 0,
  width: '100%',
  textAlign: 'left',
});

export const CloseSidebarButton = styled('button')({
  border: 0,
  background: 'transparent',
  // 8px padding enlarges the tap target; the matching -8px margin cancels it in
  // the layout box, so the icon stays visually put and nothing else shifts.
  padding: '8px',
  margin: '-8px',
  cursor: 'pointer',
  lineHeight: 0,
});

export const SidebarAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  fontSize: '12px',
  width: '24px',
  height: '24px',
  '&:hover': { backgroundColor: theme.palette.button.dark },
}));

export const MobileProfileBox = styled(Box)({
  padding: '8px',
  borderRadius: '4px',
  cursor: 'pointer',
});

// Accessible <button> toggle. 8px padding enlarges the tap target. Collapsed,
// the toggle is centered in the rail so the padding grows the hit area
// symmetrically; expanded, it's right-aligned, so that instance adds a -8px
// margin to cancel the padding and keep the icon at the right edge.
export const RailToggleButton = styled(IconButton)(({ theme }) => ({
  padding: '8px',
  color: theme.palette.text.secondary,
  '&:hover': { color: theme.palette.primary.main },
}));

export const MobileHeader = styled(ChatMenuStack)({
  // 30px lands the logo at the same left edge as the landing-page logo and the
  // chat content (which sit at ~30px), so the header/sidebar logo lines up with
  // them. The logo width also matches the landing logo (100px) so the glyph
  // scales identically.
  paddingLeft: '30px',
  paddingBottom: '2px',
});

// 32px slot (matches the New Chat button) so every nav icon shares a center axis.
export const NavIconSlot = styled(Box)({
  width: '32px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexShrink: 0,
});

export const SidebarLabelText = styled(Text)(({ theme }) => ({
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
  color: theme.palette.text.primary,
}));

// "Recents" list header — bold gray (#5B616E = text.secondary) per the 2026
// design. Left padding aligns it with the conversation card left edge below.
export const SectionTitle = styled(Text)(({ theme }) => ({
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
  color: theme.palette.text.secondary,
  paddingLeft: '12px',
}));

// Sized via fontSize (MUI SvgIcon defaults to 24px) so icons render at 20px.
// Gray (text.secondary) per design — only New Chat keeps the blue treatment.
export const ReportLibraryNavIcon = styled(InsertDriveFileIcon)(
  ({ theme }) => ({
    fontSize: '20px',
    color: theme.palette.text.secondary,
  })
);

export const AccountNavIcon = styled(PersonIcon)(({ theme }) => ({
  fontSize: '20px',
  color: theme.palette.text.secondary,
}));

export const NewChatAddIcon = styled(AddIcon)(({ theme }) => ({
  fontSize: '20px',
  color: theme.palette.text.contrastText,
}));

// Desktop rail: collapses by shrinking its width; labels clip/fade while the
// icon slots keep the icons in place.
export const SidebarColumn = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed: boolean }>(({ collapsed }) => ({
  height: '100%',
  justifyContent: 'space-between',
  paddingTop: '48px',
  paddingBottom: '30px',
  overflowX: 'hidden',
  overflowY: 'auto',
  ...hideScrollbar,
  width: collapsed ? '56px' : '224px',
  transition: 'width 0.3s ease-in-out',
  transitionDelay: collapsed ? '0.1s' : '0s',
}));

// Recents list container: fills the rail's remaining height and scrolls.
// Left padding (8px) + the item's own 4px marginLeft lands the conversation
// cards at 12px, flush under the "Recents" title. Right padding (12px) gives
// the scrollbar a gutter so titles don't run under it. Keeps the thin visible
// scrollbar so users can tell the list scrolls.
export const ScrollableConversationBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed?: boolean }>(({ theme, collapsed }) => ({
  flex: 1,
  minHeight: 0,
  // Collapsed rail: the conversation list is faded out, so there is nothing to
  // scroll — hide the overflow to suppress the empty scrollbar in the 56px rail.
  overflowY: collapsed ? 'hidden' : 'auto',
  padding: '0 12px 48px 0px',
  ...thinScrollbar(theme),
}));

// 12px left padding centers the 32px icon slot in the 56px collapsed rail, so
// the icon doesn't shift horizontally between states.
const rowBase = {
  display: 'flex',
  flexDirection: 'row' as const,
  alignItems: 'center',
  gap: '16px',
  width: '100%',
  padding: '0 0 0 12px',
};

export const SidebarRow = styled(Box)(({ theme }) => ({
  ...rowBase,
  cursor: 'pointer',
  '&:hover .sidebar-label-text': {
    color: theme.palette.primary.main,
  },
}));

export const SidebarStaticRow = styled(Box)(rowBase);

export const SidebarRowLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed' && prop !== 'truncate',
})<{ collapsed: boolean; truncate?: boolean }>(({ collapsed, truncate }) => ({
  // Single line so the row height doesn't change as the rail animates.
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  minWidth: 0,
  // truncate (profile only): let flex children shrink so ProfileSection's
  // ellipsis works — scoped so it never shrinks the header logo's <img>.
  ...(truncate && { '& *': { minWidth: 0 } }),
  opacity: collapsed ? 0 : 1,
  transition: 'opacity 0.2s ease-in-out',
  transitionDelay: collapsed ? '0s' : '0.2s',
}));

export const CollapsibleFooterItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed: boolean }>(({ collapsed }) => ({
  padding: '0 12px',
  overflow: 'hidden',
  maxHeight: collapsed ? 0 : '240px',
  opacity: collapsed ? 0 : 1,
  transition: 'opacity 0.2s ease-in-out, max-height 0.3s ease-in-out',
}));
